/**
 * Authorization
 *
 * @module auth
 */
import {getPublicProperty} from '@netx/core-app-next/utils/getPublicProperty';
import {publicProperties} from '@netx/core-app/collections/singleton/publicProperties';
import {appViews} from '@netx/core-app/singletons/appViews';
import {
	canRequestPasswordReset,
	canSelfRegister,
} from '@netx/core-auth-next/utils';
import {session} from '@netx/core-auth/collections/singleton/session';
import coreConfig from '@netx/core-next/config';
import {getPrefixedLogger, localStore} from '@netx/core-next/instance';
import {appRouter} from '@netx/core-next/instance/router';
import {setGlobal} from '@netx/core-next/utils/setGlobal';
import {whenReady} from '@netx/core-next/whenReady';
import {getPreference} from '@netx/core-user-next/utils/getPreference';
import {getUserLevel} from '@netx/core-user-next/utils/getUserLevel';
import {userProfile} from '@netx/core-user/collections/singletons/userProfile';
import Netx from '@netx/core/bootstrap';
// App config `Netx.auth.options`
import customConfig from './config';
import routes from './routes';

const logger = getPrefixedLogger('core:auth');

const IGNORE_SESSION_ROUTES = [
	'password-reset',
	'passwordReset',
	'password',
	'register',
];

const IGNORE_POST_LOGIN_ROUTES = IGNORE_SESSION_ROUTES.concat(['login']);

/**
 * @event module:auth#auth:init
 */
dispatcher.once('auth:init', function (options) {
	options = _.extend({}, options, customConfig);

	const NS = (Netx.auth = {
		bootstrap: [],
		options: options,
	});

	setGlobal({session});

	// Helper functions
	Object.assign(Netx, {
		/**
		 * @function
		 * @param {string} key [dot seperated] property key
		 * @param {boolean} isTrue Test if the property value is true
		 * @return {boolean|mixed}
		 */
		getPublicProperty: function (key, isTrue) {
			return publicProperties[isTrue ? 'isTrue' : 'getProperty'](key);
		},
		/**
		 * Get the current session
		 * @function
		 * @param {string} attr Attribute to retrieve (if omitted the session model is returned)
		 * @return {Netx.Models.Session|mixed}
		 */
		getSession: function (attr) {
			return attr ? session.get(attr) : session;
		},
		/**
		 * Get the current token
		 * @function
		 * @param {string} attr Attribute to retrieve (if omitted the user model is returned)
		 * @return {Netx.Models.UserProfile|mixed}
		 */
		getToken: function () {
			return session.getToken();
		},
	});

	// Create netx session
	// This will test the browser and also the remote DAM connection
	// if DAM_URL is specified in config.js using the Netx.session.testAjax() method.
	// See netx-session.js for more info
	// When this completes, it triggers
	var nxSession = session; //(Netx.session = session);

	// Wait for the session to configure (testAjax getPublicProperties return)
	nxSession.configured.then(function (props) {
		// Set the public properties returned from session configure
		publicProperties.set(props);

		/**
		 * If a users browser is not officially supported give them the option to manually
		 * override
		 * @event module:app#app:browser:override:confirmed
		 */
		dispatcher.once('app:browser:override:confirmed', function () {
			// If the user wants to pull the trigger...
			nxSession.set('browserOk', true);
			nxSession.setCookie('browserOk', true, 30);
		});

		/**
		 * When app:compatible:confirmed is triggered, we have an API connection
		 * to a compatible DAM version, and a valid session.
		 * @event module:app#app:compatible:confirmed
		 */
		dispatcher.once('app:compatible:confirmed', function () {
			// We showed them this already?
			nxSession.deleteCookie('sessionExpired');
			// Never let our session expire:
			NS.options.TICKLE_SESSION && nxSession.beTickling();
		});

		/**
		 * All required modules are initialized and we can check the state of the session
		 * and dispatch accordingly
		 * @event module:app#app:initialized
		 * @fires module:auth#auth:token:valid
		 * @fires module:auth#auth:token:invalid
		 * @fires module:auth#auth:session:valid
		 * @fires module:auth#auth:session:invalid
		 */
		dispatcher.once('app:initialized', function () {
			// Are we doing token-based authentication instead of user login?
			if (nxSession.getToken()) {
				// We have a token. Test it.
				nxSession.isValid().then(
					function () {
						// We have a valid token.
						dispatcher.trigger('auth:token:valid');
					},
					function () {
						// Token is not valid; fail gracefully
						dispatcher.trigger('auth:token:invalid');
					},
				);
			} else {
				// Do we have a valid session (are we logged in already?)
				nxSession.isValid().then(
					function () {
						// success handler == found a session!
						// Fetch the user
						userProfile.fetch().then(function () {
							dispatcher.trigger('auth:session:valid');
						});
					},
					function (err) {
						// isValid() fail handler (no valid session == user is not logged in)
						dispatcher.trigger('auth:session:invalid');
					},
				);
			}
		});

		/**
		 * Triggering app:stop will halt the application, log out the user and
		 * reload the page. Triggered due to invalid session, expired session or logout.
		 * @event module:app#app:stop
		 */
		dispatcher.once('app:stop', function () {
			// Delete the sessionKey cookie
			nxSession.stopTickling();
			nxSession.deleteCookie('sessionKey');
		});

		/**
		 * If we don't have a valid session at startup, netx:session:invalid is triggered.
		 * Check for various special cases, or else show a login form.
		 * @event module:auth#auth:session:invalid
		 * @fires module:auth#auth:user:login
		 * @fires module:auth#auth:user:login:confirmed
		 */
		dispatcher.once('auth:session:invalid', function () {
			const m = window.location.hash.match(/(\w+)/i)?.[1] ?? '';

			// Nothing to process in regards to session validity
			if (IGNORE_SESSION_ROUTES.includes(m)) return;

			const {hash} = window.location;

			// Check if the user was redirected to a custom login
			const customLoginWasTriggered =
				localStore.get(customConfig.CUSTOM_LOGIN_TRIGGERED_NAME) != null;

			// Do not store any routes that would lead a user to wind up here
			let postLoginHash = IGNORE_POST_LOGIN_ROUTES.includes(
				hash.replace(/^#/, ''),
			)
				? ''
				: hash;

			// If the user was redirected from a custom login, do not blow out the post login hash
			if (postLoginHash === '' && customLoginWasTriggered) {
				// use the already stored hash
				postLoginHash = localStore.get(customConfig.POST_LOGIN_HASH_NAME) || '';
			}

			// if we are not in a portal, or a portal has not been configured to allow public access -> login screen
			if (session.requiresLogin()) {
				// Refactor ticket - DAM-10298
				// use localStorage to save hash the user came in with for successful login
				localStore.set(customConfig.POST_LOGIN_HASH_NAME, postLoginHash);
				// remove - if we hit the custom login logic it will be set again
				localStore.remove(customConfig.CUSTOM_LOGIN_TRIGGERED_NAME);

				// DAM-11713 allow users to authenticate via NetX bypassing SSO and/or custom login
				if (_.contains(['', '#login'], hash)) {
					//incase #login already present
					appRouter
						.navigate('', {trigger: false})
						.navigate('login', {trigger: true});
					return;
				}

				// DAM-9976 support deep links via SSO
				// The system property `custom.index_page` was added for use with the original SSO implementation
				// Its use has been augemented to include custom home pages that do not utilize SSO
				// Redirection to the value of the property is handled exclusively by NetX's /index.jsp page
				if (
					!_.isUndefined(publicProperties.getProperty('custom.index_page')) &&
					publicProperties.getProperty('custom.index_page') !== ''
				) {
					// Set for 5 minutes - will get blown out on each trip through this event as well
					localStore.set(customConfig.CUSTOM_LOGIN_TRIGGERED_NAME, 1);
					localStore.expire(
						customConfig.CUSTOM_LOGIN_TRIGGERED_NAME,
						60 * 1000 * 5,
					);
					window.location.replace(window.location.origin);
					return;
				}

				// DAM-10369 support for custom login page
				// The system property `login.custom` was added to allow clients to utilize a custom authentication page
				// It is a boolean property that when true redirects unauthenticated users to /login.jsp
				// Redirection to the /login.jsp page is handled exclusively by NetX's /index.jsp page
				if (publicProperties.isTrue('login.custom')) {
					// Set for 5 minutes - will get blown out on each trip through this event as well
					localStore.set(customConfig.CUSTOM_LOGIN_TRIGGERED_NAME, 1);
					localStore.expire(
						customConfig.CUSTOM_LOGIN_TRIGGERED_NAME,
						60 * 1000 * 5,
					);
					window.location.replace(window.location.origin);
					return;
				}

				//incase #login already present
				appRouter
					.navigate('', {trigger: false})
					.navigate('login', {trigger: true});
				return;
			}

			// DEVNOTE: This seems like a questionable tactic? @spence
			// do a null auto-login if logins are not required.
			// THEM-1809 - public user (clean only) also wants deep linking
			localStore.set(customConfig.POST_LOGIN_HASH_NAME, postLoginHash);
			// login as public user
			dispatcher.trigger('auth:user:login:confirmed');
		});

		/**
		 * @event module:auth#auth:user:login:confirmed
		 * @param {object} values Form data
		 * @fires module:auth#auth:user:login:error
		 * @fires module:auth#auth:user:login:invalid
		 * @fires module:auth#auth:user:login:success
		 * @fires module:auth#auth:user:login:lockout
		 */
		// TODO: still used (now only) for public user login
		dispatcher.on('auth:user:login:confirmed', function () {
			const values = {
				password: null,
				username: null,
			};

			// Login
			userProfile.userLogin(values).then(
				function (sessionKey) {
					if (sessionKey !== '-1') {
						nxSession.setCookie('sessionKey', sessionKey, 1);
						nxSession.setKey(sessionKey);
						dispatcher.trigger('auth:user:login:success');
						// Perform a hard refresh to trigger the browsers' password saving prompt HTML-9
						window.location.reload(true);
					} else {
						dispatcher.trigger('auth:user:login:invalid');
					}
				},
				function (err) {
					if (err.code === 3006) {
						dispatcher.trigger('auth:user:login:lockout');
					} else {
						dispatcher.trigger(
							'auth:user:login:error',
							new Error(i18n.t('i18n.loginErrorOccurred')),
						);
					}
				},
			);
		});

		/**
		 * Valid session
		 * @event modeule:auth#auth:session:valid
		 */
		dispatcher.once('auth:session:valid', function () {
			// the expected route will occur at app start
			const hash = window.location.hash;
			if (IGNORE_POST_LOGIN_ROUTES.includes(hash.replace(/^#/, ''))) {
				appRouter.navigate('', {trigger: false});
			}
		});

		/**
		 * Invalid token
		 * @event modeule:auth#auth:token:invalid
		 */
		dispatcher.once('auth:token:invalid', function () {
			// Only for main app
			localStore.set(customConfig.POST_LOGIN_HASH_NAME, '');
		});

		/**
		 * Valid token
		 * @event modeule:auth#auth:token:valid
		 */
		dispatcher.once('auth:token:valid', function () {
			// Only for main app
			localStore.set(customConfig.POST_LOGIN_HASH_NAME, '');
		});

		// User login
		/**
		 * @route
		 * @fires module:auth#auth:user:login
		 */
		appRouter.on('route:auth:user:login', function () {
			// if using public user, allow login screen so you don't get stuck with it
			if (
				!(getPublicProperty('viewer.userLogin') === userProfile.get('login')) &&
				nxSession &&
				getUserLevel() > 0
			) {
				appRouter.navigate('', {trigger: true});
			} else {
				dispatcher.trigger('auth:user:login', true);
			}
		});

		/**
		 * @event module:auth#auth:user:login:expired
		 */
		dispatcher.on('auth:user:login:expired', function () {
			nxSession.set({
				loginMsg: i18n.t('i18n.youWereLoggedOut', {
					defaultValue: 'Logged out due to inactivity',
				}),
				loginMsgClass: 'has-error',
			});
		});
		/**
		 * @event module:auth#auth:user:login:lockout
		 */
		dispatcher.on('auth:user:login:lockout', function () {
			nxSession.set({
				loginMsg: i18n.t('i18n.loginLockedOutMessage'),
				loginMsgClass: 'has-error',
			});
		});

		/**
		 * @event module:auth#auth:user:login:invalid
		 */
		dispatcher.on('auth:user:login:invalid', function () {
			nxSession.set({
				loginMsg: i18n.t('i18n.loginIncorrect'),
				loginMsgClass: 'has-error',
			});
		});

		/**
		 * @event module:auth#auth:user:login:error
		 * @param {string} error Error string
		 * @fires module:app#app:alert
		 */
		dispatcher.on(
			'auth:user:login:error',
			function (error) {
				dispatcher.trigger('app:alert', error, i18n.t('i18n.error'));
			},
			window,
		);

		// User logout
		appRouter.on('route:auth:user:logout', function () {
			dispatcher.trigger('auth:user:logout');
		});
		/**
		 * @event module:auth#auth:user:logout:routed
		 * @fires module:app#app:stop
		 * @fires module:app#app:alert
		 */
		dispatcher.on('auth:user:logout', function () {
			userProfile.logout().then(
				function () {
					// DAM-9360 - old school logout page boolean property
					if (getPreference('user.logoutPage', true)) {
						window.location.href = '/logout.html';
					} else {
						// Router navigation needs to occur before 'app:stop' is triggered
						appRouter.navigate('');
						// Trigger application shutdown
						dispatcher.trigger('app:stop');
					}
				},
				function (err) {
					// Session ended before we logged out?
					dispatcher.trigger(
						'app:alert',
						i18n.t('i18n.sessionInvalid'),
						'Warning',
					);
					// Whatever the problem, log out anyway in 3 secs.
					_.delay(function () {
						appRouter.navigate('');
						dispatcher.trigger('app:stop');
					}, 3000);
				},
			);
		});

		/**
		 * SAML authentication
		 * @event module:auth#auth:user:saml:navigate
		 * @param {string} samlServiceName SAML service name
		 */
		dispatcher.on('auth:user:saml:navigate', function (samlServiceName) {
			// navigate browser to saml endpoint
			window.location.href = '/saml/' + samlServiceName;
		});

		// User registration
		appRouter.on('route:auth:user:register', function () {
			if ((nxSession && getUserLevel() > 0) || !canSelfRegister()) {
				appRouter.navigate('', {trigger: true});
				return;
			}
			dispatcher.trigger('auth:user:register');
		});

		// User registration awaitng approval
		appRouter.on('route:auth:user:register:approvalNeeded', function () {
			dispatcher.trigger('auth:user:register:awaiting:approval', true);
		});

		// User password reset
		appRouter.on('route:auth:user:password:reset', function () {
			if (getUserLevel() > 0 || !canRequestPasswordReset()) {
				appRouter.navigate('', {trigger: true});
				return;
			}
			dispatcher.trigger('auth:user:password:request', true);
		});

		appRouter.on('route:auth:user:password:change', function (login, token) {
			if (nxSession && getUserLevel() > 0) {
				appRouter.navigate('', {trigger: true});
				return;
			}
			dispatcher.trigger('auth:user:password:change', login, token, true);
		});

		/**
		 * This route contains a session key; if we've come this far, it probably has already been parsed.
		 * We should just redirect to the main app.
		 */
		appRouter.on('route:auth:user:sessionKey', function (sessionKey) {
			if (session.getKey() != sessionKey) {
				// this should never happen:
				console.warn('session key mismatch in /session route!');
				//Netx.session.setKey(key); // can't hurt.  shouldn't be necessary.
			} else {
				appRouter.reroute('', true);
			}
		});

		// DAM-6494:
		// Get the list of public API calls.
		// (We need this before we make any private API calls.)
		//
		// For now, the list is globally available as Netx.session.get('publicMethods').
		logger.silly('bootstrapping...');
		// get saml services for login
		nxSession.getSamlServices().then(function () {
			// Wait for all boostrap loads to complete.
			$.when.apply($, NS.bootstrap).then(function () {
				delete NS.bootstrap;
				logger.silly('bootstrapped');
				dispatcher.trigger('auth:extend', NS);
				dispatcher.trigger('auth:initialized', NS);
			});
		});
	});
});

export default {
	initialize(opts) {
		return new Promise((resolve, reject) => {
			logger.silly('initializing...');
			dispatcher.trigger('auth:init');
			dispatcher.once('auth:initialized', function () {
				logger.silly('initialized');
				resolve(routes);
			});
		});
	},
};
